import { Nullable } from "@tager/admin-services";
import { OptionType, SingleFileInputValueType } from "@tager/admin-ui";
import {
  MultiSelectFieldConfig,
  RepeaterFieldConfig,
  universalFieldUtils,
} from "@tager/admin-dynamic-field";
import { FieldUnion } from "@tager/admin-dynamic-field";
import {
  BooleanFieldConfig,
  ColorFieldConfig,
  NumberFieldConfig,
  SelectFieldConfig,
  StringFieldConfig,
} from "@tager/admin-dynamic-field";
import { getNameWithDepth } from "@tager/admin-services";

import {
  BrandType,
  CategoryType,
  FullFieldType,
  FullProductType,
  PriceMultipleValue,
  ProductGroupType,
  ProductTypeType,
  SearchType,
  SpecialType,
} from "@/typings/model";
import {
  ProductCreationPayload,
  ProductUpdatePayload,
} from "@/services/requests";
import {
  convertFieldUnionToArrayOfInputs,
  createFileInputValue,
  createGalleryFileInputValue,
} from "@/utils/common";

export type PriceMultipleValueInForm = {
  id: number | undefined;
  name: string;
  description: string;
  price: string;
  discountType: Nullable<OptionType>;
  onDiscount: boolean;
  discountValue: string;
};

export type FormValues = {
  title: string;
  subtitle: string;
  code: string;
  urlAlias: string;
  content: string;
  contentPrice: string;
  image: Nullable<SingleFileInputValueType>;
  productType: Nullable<OptionType<number>>;
  brand: Nullable<OptionType<number>>;

  gallery: Array<Nullable<SingleFileInputValueType>>;
  schemaImage: Nullable<SingleFileInputValueType>;
  youtubeVideo: string;
  video: Nullable<SingleFileInputValueType>;

  category: Nullable<OptionType<number>>;
  collection: Nullable<OptionType<number>>;
  productGroups: Array<OptionType<number>>;
  specialOffers: Array<OptionType<number>>;
  sameProducts: Array<{ id: number; name: string }>;

  price: string;
  onDiscount: boolean;
  discountType: Nullable<OptionType>;
  discountValue: string;
  priceMultiple: boolean;
  priceMultipleLabel: string;
  priceMultipleValues: Array<PriceMultipleValueInForm>;
  availability: Nullable<OptionType>;
  waitingDays: string;
  availableQuantity: string;

  pageDescription: string;
  openGraphImage: Nullable<SingleFileInputValueType>;
  pageTitle: string;
};

export const discountTypeOption = [
  { value: "VALUE", label: "Размер скидки" },
  { value: "PERCENT", label: "Процент скидки, %" },
  { value: "PRICE", label: "Цена со скидкой" },
];

export const availabilityOptionList = [
  { value: "AVAILABLE", label: "В наличии" },
  { value: "ON_REQUEST", label: "Под заказ" },
  { value: "OUT_OF_ORDER", label: "Нет в наличии" },
  { value: "PRE_ORDER", label: "Скоро в продаже" },
];

function convertPriceMultipleValuesToFormValues(
  priceMultipleValues: Array<PriceMultipleValue>
): Array<PriceMultipleValueInForm> {
  return priceMultipleValues
    ? priceMultipleValues.map((priceMultipleValue) => {
        const currentDiscountTypeOption = discountTypeOption?.find(
          (option) => option.value === priceMultipleValue.discountType
        );
        return {
          id: priceMultipleValue.id,
          name: priceMultipleValue.name,
          description: priceMultipleValue.description
            ? priceMultipleValue.description
            : "",
          price: String(priceMultipleValue.price),
          discountType: currentDiscountTypeOption ?? null,
          onDiscount: !!currentDiscountTypeOption,
          discountValue: String(priceMultipleValue.discountValue),
        };
      })
    : [
        {
          id: undefined,
          name: "",
          description: "",
          price: "0",
          discountType: null,
          onDiscount: false,
          discountValue: "0",
        },
      ];
}

export function convertProductToFormValues(
  product: Nullable<FullProductType>,
  categoryOption: Nullable<Array<OptionType<number>>>,
  brandOption: Nullable<Array<OptionType<number>>>,
  productGroupOption: Nullable<Array<OptionType<number>>>,
  specialOption: Nullable<Array<OptionType<number>>>
): FormValues {
  if (!product) {
    return {
      title: "",
      subtitle: "",
      code: "",
      urlAlias: "",
      content: "",
      contentPrice: "",
      image: null,
      productType: null,
      brand: null,

      gallery: [],
      schemaImage: null,
      youtubeVideo: "",
      video: null,

      category: null,
      collection: null,
      productGroups: [],
      specialOffers: [],
      sameProducts: [],

      price: "0",
      onDiscount: false,
      discountType: null,
      discountValue: "0",
      priceMultiple: false,
      priceMultipleLabel: "",
      priceMultipleValues: [],
      availability: null,
      waitingDays: "0",
      availableQuantity: "0",

      pageDescription: "",
      openGraphImage: null,
      pageTitle: "",
    };
  }

  const currentCategoryOption = product.category
    ? { value: product.category.id, label: product.category.name }
    : null;

  const currentCollectionOption = product.collection
    ? { value: product.collection.id, label: product.collection.name }
    : null;

  const currentBrandOption = product.brand
    ? { value: product.brand.id, label: product.brand.name }
    : null;

  const currentProductGroupOption = productGroupOption?.filter((option) =>
    product.productGroups.some(
      (productGroup) => productGroup?.id === option.value
    )
  );

  const currentSpecialOption = specialOption?.filter((option) =>
    product.specialOffers.some(
      (specialOffer) => specialOffer?.id === option.value
    )
  );

  const currentDiscountTypeOption = discountTypeOption?.find(
    (option) => option.value === product.discountType
  );

  const currentAvailabilityOption = availabilityOptionList?.find(
    (option) => option.value === product.availability
  );
  return {
    title: product.title ?? "",
    subtitle: product.subtitle ?? "",
    code: product.code ?? "",
    content: product.content ?? "",
    contentPrice: product.contentPrice ?? "",
    image: product.image ? createFileInputValue(product.image) : null,
    urlAlias: product.urlAlias ?? "",
    productType: null,
    brand: currentBrandOption ?? null,

    gallery: product.gallery
      ? product.gallery.map((image) => createGalleryFileInputValue(image))
      : [],
    schemaImage: product.schemaImage
      ? createFileInputValue(product.schemaImage)
      : null,
    youtubeVideo: product.youtubeVideo ?? "",
    video: product.video ? createFileInputValue(product.video) : null,

    category: currentCategoryOption ?? null,
    collection: currentCollectionOption ?? null,
    productGroups: currentProductGroupOption ?? [],
    specialOffers: currentSpecialOption ?? [],
    sameProducts: product.sameProducts ?? [],
    price: product.price ? String(product.price) : "0",
    onDiscount: !!currentDiscountTypeOption,
    discountType: currentDiscountTypeOption ?? null,
    discountValue: product.discountValue ? String(product.discountValue) : "0",
    priceMultiple: product.priceMultipleEnabled ?? false,
    priceMultipleLabel: product.priceMultipleLabel ?? "",
    priceMultipleValues: convertPriceMultipleValuesToFormValues(
      product.priceMultipleValues
    ),

    availability: currentAvailabilityOption ?? null,
    waitingDays: product.waitingDays ? String(product.waitingDays) : "0",
    availableQuantity: product.availableQuantity
      ? String(product.availableQuantity)
      : "0",

    pageDescription: product.pageDescription ?? "",
    openGraphImage: product.openGraphImage
      ? createFileInputValue(product.openGraphImage)
      : null,
    pageTitle: product.pageTitle ?? "",
  };
}

export function convertFormValuesToProductCreationPayload(
  values: FormValues
): ProductCreationPayload {
  return {
    productType: values.productType?.value ?? null,
    brand: values.brand?.value ?? null,
    category: values.category?.value ?? null,
    title: values.title,
    subtitle: values.subtitle,
  };
}

function convertPriceMultipleFormValuesToUpdateValues(
  priceMultipleValues: Array<PriceMultipleValueInForm>
): Array<PriceMultipleValue> {
  return priceMultipleValues.map((priceMultipleValue) => {
    return {
      id: priceMultipleValue.id,
      name: priceMultipleValue.name,
      description: priceMultipleValue.description,
      price: Number(priceMultipleValue.price),
      discountType: priceMultipleValue.onDiscount
        ? priceMultipleValue.discountType?.value ?? null
        : null,
      discountValue: priceMultipleValue.onDiscount
        ? Number(priceMultipleValue.discountValue)
        : null,
    };
  });
}

export function convertFormValuesToProductUpdatePayload(
  values: FormValues,
  productTypeValues: Array<FieldUnion>,
  inputsSameProductField: FieldUnion
): ProductUpdatePayload {
  const outgoingValue = productTypeValues.map((field) => ({
    value: universalFieldUtils.getOutgoingValue(field),
    name: field.config.name,
  }));

  return {
    brand: values.brand?.value ?? null,
    category: values.category?.value ?? null,
    title: values.title,
    subtitle: values.subtitle,

    content: values.content ?? "",
    contentPrice: values.contentPrice ?? "",
    image: values.image?.file.id ?? null,
    urlAlias: values.urlAlias ?? "",

    gallery:
      values.gallery.map((image) => ({
        id: image?.file.id,
        description: image?.caption,
      })) ?? [],
    schemaImage: values.schemaImage?.file.id ?? null,
    youtubeVideo: values.youtubeVideo ?? null,
    video: values.video?.file.id ?? null,

    collection: values.collection?.value ?? null,
    productGroups: values.productGroups.map(
      (productGroup) => productGroup.value
    ),
    specialOffers: values.specialOffers.map(
      (specialOffer) => specialOffer.value
    ),
    fieldValues: outgoingValue.map((value) => {
      const splitName = value.name.split("multiple");
      if (splitName.length >= 2) {
        return { fieldId: Number(splitName[0]), value: [value.value] };
      }
      return { fieldId: Number(value.name), value: value.value };
    }),
    sameProducts: convertFieldUnionToArrayOfInputs<{ product: string }>(
      inputsSameProductField
    ).map((sameProducts) => Number(sameProducts.product)),

    price: Number(values.price) ?? 0,
    discountType:
      values.onDiscount && values.discountType && !values.priceMultiple
        ? values.discountType.value
        : null,
    discountValue:
      values.onDiscount && !values.priceMultiple
        ? Number(values.discountValue)
        : null,
    priceMultipleEnabled: values.priceMultiple,
    priceMultipleLabel: values.priceMultiple ? values.priceMultipleLabel : "",
    priceMultipleValues: values.priceMultiple
      ? convertPriceMultipleFormValuesToUpdateValues(values.priceMultipleValues)
      : [],

    availability: values.availability?.value ?? null,
    waitingDays: Number(values.waitingDays) ?? null,
    availableQuantity: Number(values.availableQuantity) ?? null,

    pageDescription: values.pageDescription ?? "",
    openGraphImage: values.openGraphImage?.file.id ?? null,
    pageTitle: values.pageTitle ?? "",
  };
}

export function convertListToOption(
  convertList: Array<
    | ProductTypeType
    | CategoryType
    | BrandType
    | ProductGroupType
    | SpecialType
    | SearchType
  >
): Array<OptionType<number>> {
  return convertList.map((field) => ({
    value: field.id,
    label: field.name,
  }));
}

export function convertCategoryListToOption(
  categoryList: Array<CategoryType>
): Array<OptionType<number>> {
  return categoryList.map((category) => ({
    value: category.id,
    label: getNameWithDepth(category.name, category.depth),
    disabled: category.type === "CATALOG",
  }));
}

export function convertProductTypeFieldListToFieldConfigUnion(
  productTypeFieldList: Array<{
    field: FullFieldType;
    value: string | boolean | number;
  }>
): Array<
  | MultiSelectFieldConfig
  | SelectFieldConfig
  | NumberFieldConfig
  | BooleanFieldConfig
  | ColorFieldConfig
  | StringFieldConfig
> {
  return productTypeFieldList.map((field) => {
    const options = Array.isArray(field.field.options)
      ? field.field.options.map((option) => ({
          value: option.id,
          label: option.name,
        }))
      : [];

    return {
      type: field.field.type === "COLOR" ? "SELECT" : field.field.type,
      name: String(field.field.id),
      label: field.field.name,
      meta: { options: options, hidden: false },
    };
  });
}

export function convertSameProductToRepeaterIncomingValue(
  same: Array<{ id: number; name: string }>
): Array<Array<{ name: string; value: OptionType<string | number> }>> {
  return same.map((product) => {
    return [
      {
        name: "product",
        value: { value: product.id, label: product.name },
      },
    ];
  });
}

/** Similar Products table */

export function productFieldConfig(): RepeaterFieldConfig {
  return {
    name: "sameProducts",
    type: "REPEATER",
    label: "Похожие товары",
    meta: {
      view: "TABLE",
      defaultIsOpen: true,
    },
    fields: [
      {
        name: "product",
        type: "AJAX_SELECT",
        label: "Товар",
        meta: {
          requestUrl: "/admin/products/search",
          valueField: "id",
          labelField: "fullName",
        },
      },
    ],
  };
}
